"use client"

import { memo } from "react"
import { ArrowRight, Share2 } from "lucide-react"
import type { AgentDemoStep, MainToolCall, SubAgent } from "@/lib/agent-demo-data"
import type { ScenarioSkill } from "@/lib/demo-scenarios/scenario-skills"
import { TypingText } from "./typing-text"
import { RichCardsBlock } from "@/src/components/demo/rich-card-renderer"
import { SubAgentCard } from "./sub-agent-card"
import { ThinkingIndicator } from "./agent-thinking-block"
import { MainToolBubble } from "./agent-tool-call-block"
import { AgentAvatar } from "./agent-avatar"
import { SkillsLoader } from "./agent-skills-loader"

/* ── Follow-up suggestions ── */
function FollowUpSuggestions({ items }: { items: string[] }) {
  return (
    <div className="animate-in fade-in slide-in-from-bottom-3 duration-500 mt-2">
      <div className="grid grid-cols-2 gap-2">
        {items.map((item, i) => (
          <button
            key={i}
            className="group flex items-center gap-2 text-left text-sm text-foreground/80 liquid-glass-card rounded-xl px-3.5 py-2.5"
          >
            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
            <span className="line-clamp-1">{item}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

interface AgentMessageBubbleProps {
  step: AgentDemoStep
  idx: number
  allSteps: AgentDemoStep[]
  // For main-tools step
  mainToolsState: (MainToolCall & { status: "running" | "done" })[]
  // For subagents step
  liveSubAgents: SubAgent[]
  allSubAgentsDone: boolean
  selectedAgentId: string | null
  onAgentClick: (agentId: string) => void
  roleIcons: Record<string, React.ReactNode>
  // For first agent-reply (skills loader)
  scenarioSkills: ScenarioSkill[]
  onSkillsLoaded: () => void
  // For follow-up (publish CTA)
  onPublish: () => void
}

export const AgentMessageBubble = memo(function AgentMessageBubble({
  step,
  idx,
  allSteps,
  mainToolsState,
  liveSubAgents,
  allSubAgentsDone,
  selectedAgentId,
  onAgentClick,
  roleIcons,
  scenarioSkills,
  onSkillsLoaded,
  onPublish,
}: AgentMessageBubbleProps) {
  if (step.type === "user") {
    return (
      <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-primary px-4 py-2.5 shadow-sm">
          <p className="text-sm text-primary-foreground leading-relaxed">{step.content}</p>
        </div>
      </div>
    )
  }

  if (step.type === "agent-reply") {
    const isFirstReply = idx === allSteps.findIndex(s => s.type === "agent-reply")
    return (
      <div>
        <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 flex gap-3">
          <AgentAvatar />
          <div className="flex-1 max-w-[85%]">
            <div className="rounded-2xl rounded-tl-sm bg-secondary/50 backdrop-blur-sm border border-border/40 px-4 py-2.5 shadow-sm">
              <p className="text-sm text-foreground leading-relaxed">
                <TypingText text={step.content!} speed={35} />
              </p>
            </div>
            {step.richCards && <RichCardsBlock cards={step.richCards} />}
          </div>
        </div>
        {/* Skills loader — stacks orbs below first agent reply */}
        {isFirstReply && scenarioSkills.length > 0 && (
          <div className="flex gap-3 mt-2">
            <div className="h-7 w-7 shrink-0" aria-hidden />
            <div className="flex-1 max-w-[85%]">
              <SkillsLoader skills={scenarioSkills} onComplete={onSkillsLoaded} />
            </div>
          </div>
        )}
      </div>
    )
  }

  if (step.type === "main-tools") {
    return (
      <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 flex gap-3">
        <div className="mt-0.5 h-7 w-7 shrink-0" aria-hidden />
        <div className="flex max-w-[85%] flex-col gap-2">
          {mainToolsState.map((tool, i) => (
            <MainToolBubble key={i} tool={tool} />
          ))}
        </div>
      </div>
    )
  }

  if (step.type === "agent-thinking") {
    return (
      <div className="flex gap-3">
        <AgentAvatar pulse={!allSubAgentsDone} />
        <ThinkingIndicator
          text={step.content!}
          textAfter={step.contentAfter}
          allDone={allSubAgentsDone}
        />
      </div>
    )
  }

  if (step.type === "subagents") {
    return (
      <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
        <div className="flex gap-3">
          <AgentAvatar pulse={!allSubAgentsDone} />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2.5">
              <span className="text-xs font-black text-primary tracking-wider whitespace-nowrap">任务分发</span>
              <div className="flex-1 h-px bg-primary/25 dark:bg-primary/20" />
            </div>
            <div className="flex flex-col gap-1.5">
              {liveSubAgents.map((agent) => (
                <SubAgentCard
                  key={agent.id}
                  agent={agent}
                  roleIcon={roleIcons[agent.role]}
                  isSelected={selectedAgentId === agent.id}
                  onClick={() => onAgentClick(agent.id)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (step.type === "summary") {
    const items = step.summaryItems ?? ["所有子任务已完成"]
    const footer = step.summaryFooter ?? "如需调整，请随时告诉我。"
    return (
      <div className="animate-in fade-in slide-in-from-bottom-3 duration-500 flex gap-3">
        <AgentAvatar />
        <div className="flex-1 overflow-hidden">
          <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-secondary/50 backdrop-blur-sm border border-border/40 px-4 py-3 shadow-sm">
            <p className="text-sm text-foreground leading-relaxed mb-3">
              <TypingText text="所有任务已顺利完成，以下是执行摘要：" speed={30} />
            </p>
            <div className="flex flex-col gap-1.5 text-sm text-foreground/85 leading-relaxed">
              {items.map((item, i) => (
                <p key={i}>{i + 1}. {item}</p>
              ))}
            </div>
            <p className="text-sm text-foreground/70 mt-3">
              {footer}
            </p>
            {step.richCards && <RichCardsBlock cards={step.richCards} />}
          </div>
        </div>
      </div>
    )
  }

  if (step.type === "follow-up") {
    return (
      <div className="ml-10">
        <FollowUpSuggestions items={step.followUps!} />
        {/* Publish to community CTA — dopamine gradient card */}
        <div className="animate-in fade-in slide-in-from-bottom-2 duration-500 mt-4">
          <button
            type="button"
            onClick={onPublish}
            className="publish-cta-card group flex items-center gap-3 text-sm w-full rounded-2xl px-4 py-4 border border-white/10 transition-all duration-300 hover:scale-[1.01] active:scale-[0.99]"
          >
            <div className="publish-cta-icon flex items-center justify-center h-9 w-9 rounded-xl shrink-0">
              <Share2 className="h-4 w-4 text-white drop-shadow-sm" />
            </div>
            <span className="flex-1 text-left">
              <span className="font-semibold text-white/95 text-xs">总结并发布到社区</span>
              <span className="block text-caption text-white/60 mt-0.5">
                将这次对话的工作流分享给其他用户一键复制
              </span>
            </span>
            <ArrowRight className="h-4 w-4 text-white/40 group-hover:text-white/80 group-hover:translate-x-1 transition-all duration-300 shrink-0" />
          </button>
        </div>
      </div>
    )
  }

  return null
})
